import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import { UserPlus, Edit, Trash2 } from "lucide-react";
import DebouncedSearchInput from "../../components/common/DebouncedSearchInput";
import SortableTableHeader from "../../components/Table/SortableTableHeader";
import CreateCustomerModal from "./CreateCustomerModal";
import UpdateCustomerModal from "./UpdateCustomerModal";
import { toast } from "react-toastify";

const Customers = () => {
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const isSuperAdmin = activeSession?.user?.role === "superadmin";

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [search, setSearch] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination states if needed, though mostly simple lists are expected here based on snippet
  const fetchCustomers = async (search_query = "", branch_id = "", sort_by = null, sort_order = "desc") => {
    setLoading(true);
    try {
      const sortParam = sort_by ? `&sort_by=${sort_by}&sort_order=${sort_order}` : "";
      let url = `/company/customers/?search=${search_query}${sortParam}`;
      if (isSuperAdmin && branch_id) {
        url += `&branch=${branch_id}`;
      }
      const data = await requestWithAuth(url, "GET");
      setCustomers(data?.results || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(`/company/branches/?pageSize=100`, "GET");
      setBranches(data?.results || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchBranches();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchCustomers(search, selectedBranch, sortBy, sortOrder);
  }, [search, selectedBranch, sortBy, sortOrder]);

  const handleSearch = (query) => {
    setSearch(query);
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      await requestWithAuth(`/company/customers/${id}/`, "DELETE");
      toast.success("Customer deleted successfully");
      fetchCustomers(search, selectedBranch);
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error(error?.response?.data?.message || "Failed to delete customer");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <span>System</span>
              <span>/</span>
              <span className="text-gray-900 font-medium">Customers</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
          </div>
          {!isSuperAdmin && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
            >
              <UserPlus size={20} />
              Create Customer
            </button>
          )}
        </div>

        {/* Filters & Table Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              {isSuperAdmin && (
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[200px]"
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              )}
              <DebouncedSearchInput
                onSearch={handleSearch}
                placeholder="Search by name or phone..."
                delay={500}
                className="flex-1 max-w-md"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading customers...</span>
                </div>
              </div>
            ) : customers.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <SortableTableHeader
                      label="Name"
                      fieldKey="name"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      label="Phone Number"
                      fieldKey="phone_number"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Branch
                    </th>
                    <SortableTableHeader
                      label="Description"
                      fieldKey="description"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    {!isSuperAdmin && (
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {customers.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-sm font-medium text-gray-900">
                        {c.name}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {c.phone_number}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {c.branch_name}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 max-w-xs truncate">
                        {c.description}
                      </td>
                      {!isSuperAdmin && (
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedCustomer(c);
                                setIsUpdateModalOpen(true);
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                            >
                              <Edit size={14} />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(c.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm">No customers found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateCustomerModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => fetchCustomers(search, selectedBranch)}
      />

      <UpdateCustomerModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedCustomer(null);
        }}
        onSuccess={() => fetchCustomers(search, selectedBranch)}
        customer={selectedCustomer}
      />
    </div>
  );
};

export default Customers;
